// What's new — client for the product-updates feed.
//
//   GET /v1/me/product-updates        published updates for the active org
//   PUT /v1/me/product-updates/seen   marks everything seen
//
// The api does the filtering (app entitlements) and the unseen flags;
// nothing is derived here. Zod `issues[]` are walked by jsonOrThrow
// (memory: feedback_surface_zod_issues_in_client).
//
// `useProductUpdates()` keeps a short in-memory cache shared by every
// caller, because the shell — and with it the Help menu — remounts on
// each page navigation.

import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { jsonOrThrow } from './apiError';
import type { AppKey } from './apps';

const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8080';

/** How long a fetched feed is reused across remounts. */
const CACHE_TTL_MS = 2 * 60 * 1000;

export interface ProductUpdateItem {
  id: string;
  title: string;
  /** Markdown. */
  body: string;
  /** Empty = all apps. */
  apps: AppKey[];
  link: string | null;
  publishedAt: number;
  unseen: boolean;
}

export interface ProductUpdatesFeed {
  updates: ProductUpdateItem[];
  seenAt: number | null;
  unseenCount: number;
}

export async function fetchProductUpdates(idToken: string): Promise<ProductUpdatesFeed> {
  const res = await fetch(`${API_URL}/v1/me/product-updates`, {
    headers: { authorization: `Bearer ${idToken}` },
  });
  return jsonOrThrow<ProductUpdatesFeed>(res);
}

export async function markProductUpdatesSeen(idToken: string): Promise<{ seenAt: number }> {
  const res = await fetch(`${API_URL}/v1/me/product-updates/seen`, {
    method: 'PUT',
    headers: { authorization: `Bearer ${idToken}` },
  });
  return jsonOrThrow<{ seenAt: number }>(res);
}

// -------- shared cache --------

interface CacheEntry {
  key: string;
  at: number;
  feed: ProductUpdatesFeed;
}

let cache: CacheEntry | null = null;
let inflight: { key: string; promise: Promise<ProductUpdatesFeed> } | null = null;
const listeners = new Set<(feed: ProductUpdatesFeed) => void>();

function publish(key: string, feed: ProductUpdatesFeed) {
  cache = { key, at: Date.now(), feed };
  for (const l of listeners) l(feed);
}

/** Same feed with every item marked seen — what the reader sees right
 *  after opening the panel, before (and regardless of) the refetch. */
export function markAllSeen(feed: ProductUpdatesFeed, seenAt: number): ProductUpdatesFeed {
  return {
    seenAt,
    unseenCount: 0,
    updates: feed.updates.map((u) => (u.unseen ? { ...u, unseen: false } : u)),
  };
}

export interface UseProductUpdates {
  updates: ProductUpdateItem[];
  unseenCount: number;
  loading: boolean;
  error: string | null;
  /** Records "seen" on the server and clears the unseen marks locally.
   *  Resolves false (with `error` set) when the server write failed. */
  markSeen: () => Promise<boolean>;
}

export function useProductUpdates(): UseProductUpdates {
  const { session } = useAuth();
  // Cache per person + org: switching org changes which apps' news shows.
  const key = session ? `${session.uid}:${session.activeOrgId ?? ''}` : '';
  const fresh = cache && cache.key === key ? cache.feed : null;
  const [feed, setFeed] = useState<ProductUpdatesFeed | null>(fresh);
  const [loading, setLoading] = useState(!fresh && !!session);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onChange = (f: ProductUpdatesFeed) => setFeed(f);
    listeners.add(onChange);
    return () => {
      listeners.delete(onChange);
    };
  }, []);

  useEffect(() => {
    if (!session || !key) return;
    if (cache && cache.key === key && Date.now() - cache.at < CACHE_TTL_MS) {
      setFeed(cache.feed);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    if (!inflight || inflight.key !== key) {
      const promise = session.user.getIdToken().then(fetchProductUpdates);
      inflight = { key, promise };
      promise
        .then((f) => publish(key, f))
        .catch(() => undefined)
        .finally(() => {
          if (inflight?.promise === promise) inflight = null;
        });
    }
    inflight.promise
      .then((f) => {
        if (!cancelled) {
          setFeed(f);
          setError(null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [session, key]);

  const markSeen = useCallback(async (): Promise<boolean> => {
    if (!session || !feed || feed.unseenCount === 0) return true;
    try {
      const idToken = await session.user.getIdToken();
      const { seenAt } = await markProductUpdatesSeen(idToken);
      publish(key, markAllSeen(feed, seenAt));
      setError(null);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      return false;
    }
  }, [session, feed, key]);

  return {
    updates: feed?.updates ?? [],
    unseenCount: feed?.unseenCount ?? 0,
    loading,
    error,
    markSeen,
  };
}
